import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { dimensionH, dimensionV, fits, type Frame, frameFor, header, note, textWidth, TYPE, wrapText } from './kit.js';

export interface MeasuredRectangle {
  id: string;
  label: string;
  /** Real extents in `unit`, never pixels: the compiler alone turns them into length. */
  measuredWidth: number;
  measuredHeight: number;
}

export interface DerivedMeasurement {
  id: string;
  label: string;
  /** The arithmetic, written out so the reader can check it. */
  expression: string;
  /** What the result is being compared against, if anything. */
  compare?: string;
}

export interface MeasurementDimensionSpec extends PrimitiveSpec {
  family: 'measurement-dimension';
  view: 'orthographic';
  subject: string;
  unit: string;
  object: MeasuredRectangle;
  /** A second measured rectangle nested in the first, sharing its origin corner. */
  inset?: MeasuredRectangle;
  derived?: DerivedMeasurement[];
  /** A reference length drawn to the same scale. */
  scaleBar: number;
  title: string;
  subtitle: string;
}

export const measurementObjectId = (id: string): string => `dimension.object.${id}`;
export const measurementDerivedId = (id: string): string => `dimension.derived.${id}`;
export const MEASUREMENT_WIDTH_ID = 'dimension.width';
export const MEASUREMENT_HEIGHT_ID = 'dimension.height';
export const MEASUREMENT_INSET_WIDTH_ID = 'dimension.inset.width';
export const MEASUREMENT_SCALE_ID = 'dimension.scale';

interface Metrics {
  frame: Frame;
  dimensionLane: number;
  drawTop: number;
  drawBottom: number;
  scale: number;
  objectX: number;
  objectY: number;
  objectW: number;
  objectH: number;
  derivedX: number;
  derivedW: number;
  derivedInline: boolean;
  footerY: number;
  required: number;
}

function metrics(spec: MeasurementDimensionSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const dimensionLane = 40;
  const drawTop = frame.top + 6;
  const footerY = spec.viewport.height - 20;
  const drawBottom = footerY - 46;
  // Wide screens put the derived arithmetic beside the drawing, narrow ones
  // below it; either way the drawing keeps one scale factor.
  const derivedW = frame.tier === 'wide' ? 214 : 0;
  const derivedInline = derivedW > 0;
  const availableW = frame.contentWidth - dimensionLane - (derivedInline ? derivedW + 20 : 0);
  // Narrow tiers lay the page out from the bottom up: the derived arithmetic and
  // the width dimension claim exactly the space they need, and the drawing takes
  // all of the rest rather than leaving a band of nothing under it.
  const derivedRows = derivedInline ? 0 : (spec.derived?.length ?? 0);
  const stackedTop = footerY - 30 - derivedRows * 46;
  const availableH = derivedInline
    ? drawBottom - drawTop - dimensionLane
    : stackedTop - 36 - 24 - drawTop;
  const scale = Math.min(availableW / spec.object.measuredWidth, availableH / spec.object.measuredHeight);
  const objectW = spec.object.measuredWidth * scale;
  const objectH = spec.object.measuredHeight * scale;
  const objectX = frame.margin + dimensionLane;
  const objectY = drawTop + Math.max(0, (availableH - objectH) / 2);
  return {
    frame, dimensionLane, drawTop, drawBottom, scale, objectX, objectY, objectW, objectH,
    derivedX: frame.margin + frame.contentWidth - derivedW,
    derivedW,
    derivedInline,
    footerY,
    required: 380,
  };
}

/**
 * A dimensioned drawing has one job: the number and the length must agree.
 *
 * Every other figure can stretch to fit its box. This one cannot — a single
 * scale factor governs every extent, so a reader can lay a ruler on the page,
 * read the scale bar, and recover the real size. That is the whole content of
 * the primitive: extension lines that leave the feature, an arrow-terminated
 * measure line, a unit-bearing label, and a scale that ties them to the world.
 */
export function compileMeasurementDimension(spec: MeasurementDimensionSpec): ResolvedFigure {
  validateMeasurementDimension(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const objectSemantic = measurementObjectId(spec.object.id);
  const objectRight = m.objectX + m.objectW;
  const objectBottom = m.objectY + m.objectH;

  header(builder, frame, 'dimension', `MEASURED · ${spec.subject.toUpperCase()}`, [
    `DRAWN AT ${formatScale(m.scale)} · EVERY EXTENT ON ONE SCALE`,
    `SCALE ${formatScale(m.scale)}`,
  ]);

  builder.rect(`${objectSemantic}.face`, objectSemantic, 'surface', box(m.objectX, m.objectY, m.objectW, m.objectH), {
    radius: 2,
    label: `${spec.object.label}: ${format(spec.object.measuredWidth)} × ${format(spec.object.measuredHeight)} ${spec.unit}`,
    interactive: true,
  });
  builder.text(`${objectSemantic}.name`, objectSemantic, 'structure', point(m.objectX + m.objectW - 10, m.objectY + 20), spec.object.label, {
    size: TYPE.heading, mono: true, weight: 700, align: 'end',
  });
  builder.anchor(`${objectSemantic}.origin`, objectSemantic, point(m.objectX, objectBottom), 'left');
  builder.anchor(`${objectSemantic}.corner`, objectSemantic, point(objectRight, m.objectY), 'right');
  builder.hit(objectSemantic, box(m.objectX, m.objectY, m.objectW, m.objectH));

  if (spec.inset) {
    const insetSemantic = measurementObjectId(spec.inset.id);
    const insetW = spec.inset.measuredWidth * m.scale;
    const insetH = spec.inset.measuredHeight * m.scale;
    // The inset shares the object's bottom-left origin, so the two can be compared.
    const insetY = objectBottom - insetH;
    builder.rect(`${insetSemantic}.face`, insetSemantic, 'active', box(m.objectX, insetY, insetW, insetH), {
      radius: 2,
      label: `${spec.inset.label}: ${format(spec.inset.measuredWidth)} × ${format(spec.inset.measuredHeight)} ${spec.unit}`,
      interactive: true,
    });
    builder.text(`${insetSemantic}.name`, insetSemantic, 'structure', point(m.objectX + 10, insetY + 20), spec.inset.label, {
      size: TYPE.heading, mono: true, weight: 700,
    });
    builder.line(`${insetSemantic}.fold`, insetSemantic, 'construction', point(m.objectX, insetY), point(m.objectX + insetW, insetY), { dash: '6 4' });
    builder.anchor(`${insetSemantic}.corner`, insetSemantic, point(m.objectX + insetW, insetY), 'right');
    builder.hit(insetSemantic, box(m.objectX, insetY, insetW, insetH));
    builder.relation(`${insetSemantic}.nested`, 'fits-inside', insetSemantic, objectSemantic, `${spec.inset.label} is ${format(spec.inset.measuredWidth)} × ${format(spec.inset.measuredHeight)} ${spec.unit}`);

    dimensionH(builder, MEASUREMENT_INSET_WIDTH_ID, m.objectX, m.objectX + insetW, insetY - 16, insetY, `${format(spec.inset.measuredWidth)} ${spec.unit}`);
  }

  // The two extents of the object itself.
  dimensionH(builder, MEASUREMENT_WIDTH_ID, m.objectX, objectRight, objectBottom + 24, objectBottom, `${format(spec.object.measuredWidth)} ${spec.unit}`, { above: false });
  dimensionV(builder, MEASUREMENT_HEIGHT_ID, m.objectY, objectBottom, m.objectX - 22, m.objectX, `${format(spec.object.measuredHeight)} ${spec.unit}`);
  builder.relation(`${MEASUREMENT_WIDTH_ID}.measures`, 'measures', MEASUREMENT_WIDTH_ID, objectSemantic, `${spec.object.label} is ${format(spec.object.measuredWidth)} ${spec.unit} wide`);
  builder.relation(`${MEASUREMENT_HEIGHT_ID}.measures`, 'measures', MEASUREMENT_HEIGHT_ID, objectSemantic, `${spec.object.label} is ${format(spec.object.measuredHeight)} ${spec.unit} tall`);

  // The scale bar: the drawing's own statement of what one length means.
  const barW = spec.scaleBar * m.scale;
  const barY = m.footerY - 22;
  const barX = m.frame.margin + m.frame.contentWidth - barW;
  builder.line(`${MEASUREMENT_SCALE_ID}.bar`, MEASUREMENT_SCALE_ID, 'dimension', point(barX, barY), point(barX + barW, barY));
  builder.line(`${MEASUREMENT_SCALE_ID}.cap.start`, MEASUREMENT_SCALE_ID, 'dimension', point(barX, barY - 5), point(barX, barY + 5));
  builder.line(`${MEASUREMENT_SCALE_ID}.cap.end`, MEASUREMENT_SCALE_ID, 'dimension', point(barX + barW, barY - 5), point(barX + barW, barY + 5));
  builder.rect(`${MEASUREMENT_SCALE_ID}.half`, MEASUREMENT_SCALE_ID, 'depth', box(barX, barY - 3, barW / 2, 6));
  builder.text(`${MEASUREMENT_SCALE_ID}.label`, MEASUREMENT_SCALE_ID, 'annotation', point(barX + barW / 2, barY + 18), `${format(spec.scaleBar)} ${spec.unit}`, {
    size: TYPE.micro, mono: true, weight: 700, align: 'middle',
  });
  builder.anchor(`${MEASUREMENT_SCALE_ID}.start`, MEASUREMENT_SCALE_ID, point(barX, barY), 'left');
  builder.hit(MEASUREMENT_SCALE_ID, box(barX - 6, barY - 14, barW + 12, 36));
  builder.relation(`${MEASUREMENT_SCALE_ID}.states`, 'sets-scale', MEASUREMENT_SCALE_ID, objectSemantic, `${format(spec.scaleBar)} ${spec.unit} at ${formatScale(m.scale)}`);

  // Derived measurements: arithmetic the reader can check against the drawing.
  const derived = spec.derived ?? [];
  derived.forEach((item, index) => {
    const semanticId = measurementDerivedId(item.id);
    const width = m.derivedInline ? m.derivedW : frame.contentWidth;
    const x = m.derivedInline ? m.derivedX : frame.margin;
    const y = m.derivedInline ? m.drawTop + 8 + index * 68 : objectBottom + 60 + index * 46;
    builder.text(`${semanticId}.label`, semanticId, 'annotation', point(x, y), item.label.toUpperCase(), {
      size: TYPE.micro, mono: true, weight: 700, uppercase: true,
    });
    const expressionLines = m.derivedInline ? wrapText(item.expression, TYPE.label, width, true) : [item.expression];
    expressionLines.forEach((line, lineIndex) => {
      builder.text(`${semanticId}.expression.${lineIndex}`, semanticId, 'structure', point(x, y + 18 + lineIndex * 16), line, {
        size: fits(line, TYPE.label, width, true) ? TYPE.label : TYPE.micro,
        mono: true,
        weight: 700,
      });
    });
    if (item.compare) {
      builder.text(`${semanticId}.compare`, semanticId, 'annotation', point(x, y + 18 + expressionLines.length * 16), item.compare, {
        size: TYPE.micro, mono: true, weight: 500,
      });
    }
    builder.anchor(`${semanticId}.at`, semanticId, point(x, y), 'left');
    builder.hit(semanticId, box(x - 4, y - 14, Math.max(width, textWidth(item.expression, TYPE.label, true)) + 8, 24 + expressionLines.length * 16 + (item.compare ? 14 : 0)));
    builder.relation(`${semanticId}.derives`, 'derives-from', semanticId, objectSemantic, `${item.label}: ${item.expression}`);
  });

  const notes = ['ONE SCALE FACTOR · NO EXTENT ROUNDED TO FIT', 'ONE SCALE FACTOR · NOTHING ROUNDED', 'ONE SCALE FACTOR'];
  const noteRoom = frame.contentWidth - barW - 24;
  note(builder, 'dimension.note', 'dimension', point(frame.margin, m.footerY - 4), notes.find((candidate) => fits(candidate, TYPE.micro, noteRoom, true, true)) ?? notes[notes.length - 1]!);
  if (m.scale < 0.4) warnings.push('The drawing is small enough that fine features are below the readable floor.');
  if (!m.derivedInline && derived.length > 1) warnings.push('Derived arithmetic is stacked below the drawing at this width.');

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: `scale:${formatScale(m.scale)}`,
    active: [spec.inset ? measurementObjectId(spec.inset.id) : objectSemantic, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateMeasurementDimension(spec: MeasurementDimensionSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'measurement-dimension' || spec.view !== 'orthographic') throw new Error('measurement-dimension: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('measurement-dimension: provenance is required');
  if (!spec.subject.trim() || !spec.unit.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('measurement-dimension: subject, unit, title and subtitle are required');
  for (const rectangle of [spec.object, ...(spec.inset ? [spec.inset] : [])]) {
    if (!/^[a-z][a-z0-9-]*$/.test(rectangle.id)) throw new Error(`measurement-dimension: invalid rectangle id ${rectangle.id}`);
    if (!rectangle.label.trim() || rectangle.label.length > 14) throw new Error(`measurement-dimension: ${rectangle.id} needs a label of 1…14 characters`);
    if (!Number.isFinite(rectangle.measuredWidth) || !Number.isFinite(rectangle.measuredHeight) || rectangle.measuredWidth <= 0 || rectangle.measuredHeight <= 0) {
      throw new Error(`measurement-dimension: ${rectangle.id} needs positive measured extents`);
    }
  }
  if (spec.inset) {
    if (spec.inset.id === spec.object.id) throw new Error('measurement-dimension: the inset needs its own id');
    if (spec.inset.measuredWidth > spec.object.measuredWidth || spec.inset.measuredHeight > spec.object.measuredHeight) {
      throw new Error('measurement-dimension: the inset must fit inside the object it is drawn in');
    }
  }
  if (!Number.isFinite(spec.scaleBar) || spec.scaleBar <= 0 || spec.scaleBar > spec.object.measuredWidth) {
    throw new Error('measurement-dimension: the scale bar must be a positive length no longer than the object');
  }
  const ids = new Set<string>();
  for (const item of spec.derived ?? []) {
    if (!/^[a-z][a-z0-9-]*$/.test(item.id)) throw new Error(`measurement-dimension: invalid derived id ${item.id}`);
    if (ids.has(item.id)) throw new Error(`measurement-dimension: duplicate derived id ${item.id}`);
    ids.add(item.id);
    if (!item.label.trim() || !item.expression.trim()) throw new Error(`measurement-dimension: derived ${item.id} needs a label and an expression`);
  }
  if ((spec.derived ?? []).length > 3) throw new Error('measurement-dimension: at most three derived measurements');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`measurement-dimension: this drawing needs at least ${Math.ceil(required)}px of height`);
}

function format(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
}

function formatScale(scale: number): string {
  if (scale >= 1) return `${Math.round(scale * 100) / 100} : 1`;
  return `1 : ${Math.round((1 / scale) * 100) / 100}`;
}

export const measurementScaleOf = (spec: MeasurementDimensionSpec): number => metrics(spec).scale;
export const measurementDrawnLengths = (spec: MeasurementDimensionSpec): Array<{ id: string; value: number; drawn: number }> => {
  const m = metrics(spec);
  const lengths = [
    { id: MEASUREMENT_WIDTH_ID, value: spec.object.measuredWidth, drawn: m.objectW },
    { id: MEASUREMENT_HEIGHT_ID, value: spec.object.measuredHeight, drawn: m.objectH },
  ];
  if (spec.inset) lengths.push({ id: MEASUREMENT_INSET_WIDTH_ID, value: spec.inset.measuredWidth, drawn: spec.inset.measuredWidth * m.scale });
  lengths.push({ id: MEASUREMENT_SCALE_ID, value: spec.scaleBar, drawn: spec.scaleBar * m.scale });
  return lengths;
};
